/**
 * Restaurant Websites — Kitxens flagship service page.
 *
 * Premium visual landing (Index AI / Digital Branding tier) — dark hero,
 * black narrative sections, orange accents, device mockups, no walls of text.
 *
 * Editorial rule: numbers are portfolio ranges + honest benchmarks.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowUpRight,
  Globe,
  MonitorSmartphone,
  Tablet,
  Smartphone,
  Cpu,
  Zap,
  Search,
  Gauge,
  Layers,
  BookOpen,
  Calendar,
  ShoppingCart,
  ShieldCheck,
  Languages,
  Sparkles,
  MapPin,
  CheckCircle2,
  XCircle,
  Bot,
} from 'lucide-react';
import SEO from '../components/SEO';
import HeroImage from '../components/HeroImage';
import ContinueExploring from '../components/ContinueExploring';
import * as BI from '../components/BrandIcons';
import { heroFor } from '../data/serviceImages';
import RelatedResources from '../components/RelatedResources';
import { useT } from '../i18n';

const COPY = {
  en: {
    seo_title: 'Restaurant Websites | Kitxens',
    seo_desc:
      'SaaS-grade restaurant websites — mobile-first, tuned for AI search, integrated with your POS, reservations and direct ordering, operated by Kitxens.',
    eyebrow: 'RESTAURANT WEBSITES',
    title: 'A restaurant website that behaves like a product.',
    tagline: 'Fast. Findable. Integrated. Built for the guest journey, not for a portfolio slide.',
    intro:
      'Kitxens designs, builds and operates restaurant websites as living products — connected to your POS, reservations, direct ordering, Google Business, analytics and AI search — under one continuous editorial standard.',
    ctaA: 'Get a Free Audit',
    ctaB: 'Talk to our Team',

    s1_eyebrow: 'THE ANTI-BROCHURE',
    s1_title: 'A restaurant website should not be a brochure.',
    s1_body: 'It should behave like a product — fast on mobile, discoverable in AI, connected to your operation.',

    s2_eyebrow: 'THE WEBSITE STACK',
    s2_title: 'Eight surfaces. One connected product.',
    s2_body: 'Every component wired into the same platform so menus, hours, orders and reservations stay in sync.',

    s3_eyebrow: 'DEVICE SHOWCASE',
    s3_title: 'Every screen tuned to the moment of decision.',
    s3_body: 'Desktop, tablet and mobile designs share one system — no ugly mobile fallback.',

    s4_eyebrow: 'SPEED & AI SEARCH',
    s4_title: 'Under 2 seconds on mobile. Legible to AI.',
    s4_body: 'Structured data (Restaurant, Menu, LocalBusiness), edge hosting and clean semantic HTML — engineered to be quoted by ChatGPT, Gemini and Google AI Overviews.',

    s5_eyebrow: 'ECOSYSTEM',
    s5_title: 'Wired into every platform your restaurant runs on.',

    s6_eyebrow: 'MULTI-LOCATION',
    s6_title: 'From one location to two hundred.',
    s6_body: 'A single admin layer scales the same brand across every location — menus, hours, promotions, staff, always in sync.',

    s7_eyebrow: 'OUTCOMES',
    s7_title: 'What a real restaurant website returns.',
    s7_body: 'Ranges observed across the Kitxens portfolio in the first two quarters after launch.',

    s8_title: "Let's launch a website that earns its cover.",
    s8_body: 'A free audit maps your current site against the Kitxens standard and shows the direct-revenue upside.',
    s8_cta: 'Get a Free Audit',
  },
  es: {
    seo_title: 'Sitios Web para Restaurantes | Kitxens',
    seo_desc:
      'Sitios web de restaurante calidad SaaS — mobile-first, tuneados para búsqueda IA, integrados con tu POS, reservas y pedidos directos, operados por Kitxens.',
    eyebrow: 'SITIOS WEB PARA RESTAURANTES',
    title: 'Un sitio de restaurante que se comporta como producto.',
    tagline: 'Rápido. Encontrable. Integrado. Construido para el journey del cliente, no para el portfolio.',
    intro:
      'Kitxens diseña, construye y opera sitios de restaurante como productos vivos — conectados con tu POS, reservas, pedido directo, Google Business, analítica y búsqueda IA — bajo un mismo estándar editorial.',
    ctaA: 'Solicita Auditoría Gratis',
    ctaB: 'Habla con el Equipo',

    s1_eyebrow: 'ANTI-FOLLETO',
    s1_title: 'Un sitio de restaurante no debería ser un folleto.',
    s1_body: 'Debería comportarse como producto — rápido en móvil, descubrible en IA, conectado a tu operación.',

    s2_eyebrow: 'EL STACK DEL SITIO',
    s2_title: 'Ocho superficies. Un producto conectado.',
    s2_body: 'Cada componente cableado a la misma plataforma — menús, horarios, pedidos y reservas siempre sincronizados.',

    s3_eyebrow: 'MULTI-DISPOSITIVO',
    s3_title: 'Cada pantalla tuneada al momento de decisión.',
    s3_body: 'Desktop, tablet y móvil comparten un solo sistema — cero mobile fallback feo.',

    s4_eyebrow: 'VELOCIDAD Y BÚSQUEDA IA',
    s4_title: 'Bajo 2 segundos en móvil. Legible para IA.',
    s4_body: 'Datos estructurados (Restaurant, Menu, LocalBusiness), edge hosting y HTML limpio — construido para ser citado por ChatGPT, Gemini y Google AI Overviews.',

    s5_eyebrow: 'ECOSISTEMA',
    s5_title: 'Conectado a cada plataforma de la que depende tu restaurante.',

    s6_eyebrow: 'MULTI-LOCAL',
    s6_title: 'De un local a doscientos.',
    s6_body: 'Un solo panel administra la misma marca en todos los locales — menús, horarios, promociones y equipo siempre en sincronía.',

    s7_eyebrow: 'RESULTADOS',
    s7_title: 'Lo que devuelve un sitio de restaurante real.',
    s7_body: 'Rangos observados en la cartera Kitxens los primeros dos trimestres tras el lanzamiento.',

    s8_title: 'Lancemos un sitio que se gane su portada.',
    s8_body: 'Una auditoría gratuita compara tu sitio actual contra el estándar Kitxens y cuantifica el upside en ingresos directos.',
    s8_cta: 'Solicita Auditoría Gratis',
  },
};

function SectionHeader({ eyebrow, title, body, align = 'center', dark = false }) {
  return (
    <div className={`max-w-2xl ${align === 'center' ? 'mx-auto text-center' : ''}`}>
      <p className="text-[11px] tracking-[0.22em] font-bold uppercase text-[#F77F11]">{eyebrow}</p>
      <h2
        className={`mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-[-0.02em] leading-[1.1] ${
          dark ? 'text-white' : 'text-neutral-900'
        }`}
      >
        {title}
      </h2>
      {body && (
        <p className={`mt-5 text-[15.5px] sm:text-base leading-relaxed ${dark ? 'text-neutral-300' : 'text-neutral-600'}`}>
          {body}
        </p>
      )}
    </div>
  );
}

function FlowNode({ icon: Icon, label, i, dark = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
      className="group flex flex-col items-center text-center"
    >
      <div
        className={`w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-2xl flex items-center justify-center transition-all ${
          dark
            ? 'bg-white/[0.04] border border-white/10 group-hover:border-[#F77F11]'
            : 'bg-white border border-orange-100 shadow-[0_18px_50px_-30px_rgba(247,127,17,0.4)] group-hover:border-[#F77F11]'
        }`}
      >
        <Icon className="w-7 h-7 text-[#F77F11]" />
      </div>
      <p className={`mt-3 text-[13.5px] font-semibold tracking-[-0.005em] leading-tight max-w-[110px] ${dark ? 'text-neutral-200' : 'text-neutral-800'}`}>
        {label}
      </p>
    </motion.div>
  );
}

export default function RestaurantWebsitesPage() {
  const { lang } = useT();
  const c = COPY[lang] || COPY.en;
  const hero = heroFor('restaurant-websites');

  const stack = [
    { icon: Sparkles, label: lang === 'es' ? 'Diseño' : 'Design' },
    { icon: Layers, label: 'CMS' },
    { icon: BookOpen, label: lang === 'es' ? 'Menú' : 'Menu' },
    { icon: Calendar, label: lang === 'es' ? 'Reservas' : 'Reservations' },
    { icon: ShoppingCart, label: lang === 'es' ? 'Pedidos' : 'Ordering' },
    { icon: Search, label: 'SEO' },
    { icon: Bot, label: lang === 'es' ? 'Búsqueda IA' : 'AI Search' },
    { icon: Gauge, label: lang === 'es' ? 'Analítica' : 'Analytics' },
  ];

  const brochureBad = [
    { label: lang === 'es' ? 'Carga móvil > 4s' : 'Mobile load > 4s' },
    { label: lang === 'es' ? 'PDF de menú desactualizado' : 'Outdated PDF menu' },
    { label: lang === 'es' ? 'Sin datos estructurados' : 'No structured data' },
    { label: lang === 'es' ? 'Sin conexión a POS' : 'No POS connection' },
    { label: lang === 'es' ? 'Ilegible para IA' : 'Illegible to AI' },
  ];
  const productGood = [
    { label: lang === 'es' ? 'Carga móvil < 2s' : 'Mobile load < 2s' },
    { label: lang === 'es' ? 'Menú siempre en sincronía' : 'Menu always in sync' },
    { label: lang === 'es' ? 'Restaurant + Menu + LocalBusiness schema' : 'Restaurant + Menu + LocalBusiness schema' },
    { label: lang === 'es' ? 'Pedidos y reservas nativos' : 'Native ordering & reservations' },
    { label: lang === 'es' ? 'Citado por ChatGPT y Gemini' : 'Cited by ChatGPT & Gemini' },
  ];

  const ecosystem = [
    { name: 'Google', Mark: BI.GoogleMark },
    { name: 'Toast', Mark: BI.ToastMark },
    { name: 'Square', Mark: BI.SquareMark },
    { name: 'Clover', Mark: BI.CloverMark },
    { name: 'OpenTable', Mark: BI.OpenTableMark },
    { name: 'ChatGPT', Mark: BI.ChatGPTMark },
    { name: 'Gemini', Mark: BI.GeminiMark },
    { name: 'Meta', Mark: BI.MetaMark },
    { name: 'Instagram', Mark: BI.InstagramMark },
    { name: 'Stripe', Mark: BI.StripeMark },
    { name: 'DoorDash', Mark: BI.DoorDashMark },
    { name: 'Uber Eats', Mark: BI.UberEatsMark },
  ];

  const metrics = [
    { k: '< 1.8s', v: lang === 'es' ? 'Carga móvil promedio' : 'Average mobile load' },
    { k: '+38%', v: lang === 'es' ? 'Pedidos directos post-lanzamiento' : 'Direct orders post-launch' },
    { k: '+22–60%', v: lang === 'es' ? 'Tráfico orgánico local' : 'Local organic traffic' },
    { k: '95+', v: lang === 'es' ? 'Lighthouse Performance' : 'Lighthouse Performance' },
    { k: 'EN/ES', v: lang === 'es' ? 'Bilingüe de fábrica' : 'Bilingual by default' },
  ];

  const aiFeatures = [
    { icon: Search, t: lang === 'es' ? 'Datos estructurados' : 'Structured data', b: lang === 'es' ? 'Restaurant, Menu, LocalBusiness, FAQPage, OpeningHours.' : 'Restaurant, Menu, LocalBusiness, FAQPage, OpeningHours.' },
    { icon: Zap, t: lang === 'es' ? 'Edge hosting' : 'Edge hosting', b: lang === 'es' ? 'CDN global. Bajo 100ms al primer byte.' : 'Global CDN. Under 100ms to first byte.' },
    { icon: Languages, t: lang === 'es' ? 'Semántica multilingüe' : 'Multilingual semantics', b: lang === 'es' ? 'hreflang, alternates y sitemap doble EN/ES.' : 'hreflang, alternates and dual EN/ES sitemap.' },
    { icon: Bot, t: lang === 'es' ? 'Legible para IA' : 'Legible to AI', b: lang === 'es' ? 'Prosa que ChatGPT y Gemini pueden citar palabra por palabra.' : 'Prose ChatGPT and Gemini can quote verbatim.' },
    { icon: ShieldCheck, t: lang === 'es' ? 'Accesibilidad AA' : 'AA accessibility', b: lang === 'es' ? 'WCAG 2.1 AA — contrastes, foco visible, semántica.' : 'WCAG 2.1 AA — contrast, visible focus, semantics.' },
    { icon: Gauge, t: lang === 'es' ? 'Core Web Vitals verdes' : 'Green Core Web Vitals', b: lang === 'es' ? 'LCP, CLS y INP monitoreados por local, cada semana.' : 'LCP, CLS and INP monitored per location, weekly.' },
  ];

  return (
    <div className="pt-28 bg-white" data-testid="page-restaurant-websites">
      <SEO path="/services/restaurant-websites" title={c.seo_title} description={c.seo_desc} />

      {/* ═══════ 0. HERO (dark) ═══════ */}
      <section className="relative overflow-hidden bg-neutral-950 text-white pt-14 pb-16 lg:pt-24 lg:pb-24">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, #F77F11 1.2px, transparent 1.8px), radial-gradient(circle at 80% 70%, #F77F11 1.2px, transparent 1.8px)',
            backgroundSize: '140px 140px, 190px 190px',
          }}
        />
        <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-7"
            >
              <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase">{c.eyebrow}</p>
              <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] leading-[1.05]">{c.title}</h1>
              <p className="mt-5 text-xl sm:text-2xl text-neutral-200 font-semibold leading-snug max-w-2xl">{c.tagline}</p>
              <p className="mt-5 text-neutral-400 text-base sm:text-lg leading-relaxed max-w-2xl">{c.intro}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/#audit"
                  className="inline-flex items-center gap-1 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-7 rounded-full text-[15px] font-semibold shadow-sm transition-all"
                  data-testid="rw-cta-audit"
                >
                  {c.ctaA} <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
                <Link
                  to="/contact#contact-form"
                  className="inline-flex items-center gap-1 border border-white/25 hover:bg-white/10 text-white h-12 px-7 rounded-full text-[15px] font-semibold"
                  data-testid="rw-cta-talk"
                >
                  {c.ctaB} <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <HeroImage image={hero} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ 1. ANTI-BROCHURE (black) ═══════ */}
      <section className="py-20 lg:py-24 bg-neutral-900 text-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s1_eyebrow} title={c.s1_title} body={c.s1_body} dark />
          <div className="mt-14 grid md:grid-cols-2 gap-5">
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
              <p className="text-[11px] tracking-[0.22em] font-bold uppercase text-neutral-400">
                {lang === 'es' ? 'Como folleto' : 'As a brochure'}
              </p>
              <ul className="mt-6 space-y-3">
                {brochureBad.map((p) => (
                  <li key={p.label} className="flex items-start gap-3 text-neutral-300 text-[14.5px]">
                    <XCircle className="w-4 h-4 text-red-400 mt-1 shrink-0" />
                    <span>{p.label}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl border border-[#F77F11]/30 bg-[#F77F11]/[0.08] p-8 shadow-[0_20px_60px_-30px_rgba(247,127,17,0.4)]">
              <p className="text-[11px] tracking-[0.22em] font-bold uppercase text-[#F77F11]">
                {lang === 'es' ? 'Como producto Kitxens' : 'As a Kitxens product'}
              </p>
              <ul className="mt-6 space-y-3">
                {productGood.map((p) => (
                  <li key={p.label} className="flex items-start gap-3 text-neutral-200 text-[14.5px]">
                    <CheckCircle2 className="w-4 h-4 text-[#F77F11] mt-1 shrink-0" />
                    <span>{p.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ 2. STACK NODES (light) ═══════ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s2_eyebrow} title={c.s2_title} body={c.s2_body} />
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-x-2 gap-y-10">
            {stack.map((n, i) => (
              <FlowNode key={n.label} icon={n.icon} label={n.label} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 3. DEVICE SHOWCASE (dark) ═══════ */}
      <section className="py-20 lg:py-24 bg-neutral-950 text-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s3_eyebrow} title={c.s3_title} body={c.s3_body} dark />
          <div className="mt-14 grid md:grid-cols-5 gap-5 items-start">
            {[
              {
                img: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/mv6re3l7_tierra%20divina%202%20y%203%20%28adaptar%29.png',
                label: 'Desktop · Tablet · Mobile',
                alt: 'Tierra Divina restaurant website across desktop, tablet and mobile — responsive menu and online ordering built by Kitxens.',
                span: 'md:col-span-3',
              },
              {
                img: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/n7zne9s7_mesadivina1.png',
                label: 'Desktop Experience',
                alt: 'Tierra Divina restaurant website hero on a laptop — reservations and order online CTAs above the fold.',
                span: 'md:col-span-2',
              },
            ].map((d, i) => (
              <motion.figure
                key={d.label}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`group relative rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03] hover:border-[#F77F11]/60 hover:-translate-y-1 hover:shadow-[0_36px_90px_-30px_rgba(247,127,17,0.45)] transition-all duration-300 ${d.span}`}
                data-testid={`device-showcase-${i}`}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={d.img}
                    alt={d.alt}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto select-none transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                  />
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute bottom-2.5 right-2.5 flex items-center rounded-full bg-white/70 backdrop-blur-md px-2 py-0.5 shadow-sm ring-1 ring-white/50"
                  >
                    <span className="text-[9px] font-semibold tracking-[-0.005em] text-neutral-800/80">
                      kit<span className="text-[#F77F11]">x</span>ens
                    </span>
                  </span>
                </div>
                <figcaption className="p-4 flex items-center gap-2">
                  <span className="text-[11px] tracking-[0.2em] font-bold text-[#F77F11] uppercase">{d.label}</span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 4. SPEED & AI SEARCH (black) ═══════ */}
      <section className="py-20 lg:py-24 bg-neutral-900 text-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s4_eyebrow} title={c.s4_title} body={c.s4_body} dark />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {aiFeatures.map((t, i) => (
              <motion.div
                key={t.t}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:border-[#F77F11]/50 hover:bg-white/[0.06] transition-all"
              >
                <span className="inline-flex w-11 h-11 rounded-xl bg-[#F77F11]/15 items-center justify-center">
                  <t.icon className="w-5 h-5 text-[#F77F11]" />
                </span>
                <h3 className="mt-5 text-white font-semibold text-[17px] leading-snug tracking-[-0.005em]">{t.t}</h3>
                <p className="mt-2 text-neutral-400 text-[14px] leading-relaxed">{t.b}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/index-ai"
              className="inline-flex items-center gap-1.5 text-[#F77F11] hover:text-[#d8651a] font-semibold text-[15px]"
              data-testid="rw-link-index-ai"
            >
              {lang === 'es' ? 'Descubre Index AI' : 'Discover Index AI'} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════ 5. ECOSYSTEM (light, marquee) ═══════ */}
      <section className="py-20 lg:py-24 bg-white overflow-hidden">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s5_eyebrow} title={c.s5_title} />
        </div>
        <div className="relative mt-12 group [--dur:38s]">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-white to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-white to-transparent z-10" />
          <div className="flex overflow-hidden">
            <div className="flex shrink-0 rw-marquee">
              {[...ecosystem, ...ecosystem].map(({ name, Mark }, i) => (
                <div
                  key={i}
                  className="mx-6 flex items-center gap-2.5 border border-neutral-200 rounded-full px-5 h-11 bg-white hover:border-[#F77F11]/50 hover:text-[#F77F11] transition-colors"
                >
                  <Mark className="w-5 h-5" />
                  <span className="text-[13.5px] font-semibold text-neutral-700 whitespace-nowrap">{name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <style>{`
          @keyframes rw-scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .rw-marquee { animation: rw-scroll var(--dur) linear infinite; }
          .group:hover .rw-marquee { animation-play-state: paused; }
        `}</style>
      </section>

      {/* ═══════ 6. MULTI-LOCATION (light with orange tint) ═══════ */}
      <section className="py-20 lg:py-24 bg-gradient-to-b from-orange-50/40 to-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s6_eyebrow} title={c.s6_title} body={c.s6_body} />
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-x-2 gap-y-10 max-w-4xl mx-auto">
            {[
              { icon: Globe, label: '1 → 5' },
              { icon: MapPin, label: '5 → 20' },
              { icon: Layers, label: '20 → 100' },
              { icon: Sparkles, label: '100+' },
            ].map((n, i) => (
              <FlowNode key={n.label} icon={n.icon} label={n.label} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 7. RESULTS (dark) ═══════ */}
      <section className="py-20 lg:py-24 bg-neutral-950 text-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s7_eyebrow} title={c.s7_title} body={c.s7_body} dark />
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-5 gap-4">
            {metrics.map((m, i) => (
              <motion.div
                key={m.k}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="rounded-2xl bg-white/[0.04] border border-white/10 hover:border-[#F77F11]/50 transition-colors p-6"
              >
                <p className="text-[#F77F11] text-2xl sm:text-3xl font-bold tracking-[-0.02em] leading-none">{m.k}</p>
                <p className="mt-3 text-neutral-300 text-[13px] leading-relaxed">{m.v}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 8. RELATED RESOURCES ═══════ */}
      <RelatedResources serviceSlug="restaurant-websites" lang={lang} />

      {/* ═══════ 9. FINAL CTA ═══════ */}
      <section className="py-24 lg:py-32 relative overflow-hidden bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 text-white">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 30% 40%, #F77F11 1.3px, transparent 2px), radial-gradient(circle at 70% 60%, #F77F11 1.3px, transparent 2px)',
            backgroundSize: '150px 150px, 210px 210px',
          }}
        />
        <div className="relative max-w-3xl mx-auto text-center px-6">
          <h2 className="text-4xl sm:text-5xl lg:text-[52px] font-bold tracking-[-0.02em] leading-[1.05]">{c.s8_title}</h2>
          <p className="mt-6 text-neutral-300 text-lg leading-relaxed">{c.s8_body}</p>
          <Link
            to="/#audit"
            className="mt-10 inline-flex items-center gap-1.5 bg-[#F77F11] hover:bg-[#d8651a] text-white h-14 px-9 rounded-full text-[16px] font-semibold shadow-lg transition-all"
            data-testid="rw-final-cta"
          >
            {c.s8_cta} <ArrowRight className="w-5 h-5 ml-1" />
          </Link>
        </div>
      </section>

      <ContinueExploring context="restaurant-websites" />
    </div>
  );
}
